import { NominationId, TerpecaRoom } from '../models/room.model';
import { TerpecaSettings } from '../models/settings.model';
import { ApplicationStatus } from '../models/user.model';

export const ROOM_RESTRICTED_COLLECTION = 'roomRestricted';
export const MIN_AWARD_YEAR = 2018;

export interface RoomRestrictedFields {
  nominations?: NominationId[];
  resultsData?: TerpecaRoom['resultsData'];
  isNominee?: number[];
  isFinalist?: number[];
  isWinner?: number[];
  docId?: string;
}

export function canReadRoomRestricted(user?: {
  status?: ApplicationStatus;
  isOwner?: boolean;
  hasResultsAccess?: boolean;
} | null): boolean {
  return Boolean(user && (
    user.isOwner ||
    user.hasResultsAccess ||
    (user.status || ApplicationStatus.NONE) >= ApplicationStatus.APPROVER
  ));
}

export function splitRoomUpdate(update: Partial<TerpecaRoom>): {
  publicUpdate: Partial<TerpecaRoom>;
  restrictedUpdate: RoomRestrictedFields;
} {
  const publicUpdate: Partial<TerpecaRoom> = { ...update };
  const restrictedUpdate: RoomRestrictedFields = {};
  if ('nominations' in publicUpdate) {
    restrictedUpdate.nominations = publicUpdate.nominations;
    delete publicUpdate.nominations;
  }
  if ('resultsData' in publicUpdate) {
    restrictedUpdate.resultsData = publicUpdate.resultsData;
    delete publicUpdate.resultsData;
  }
  if ('isNominee' in publicUpdate) {
    restrictedUpdate.isNominee = publicUpdate.isNominee;
    delete publicUpdate.isNominee;
  }
  if ('isFinalist' in publicUpdate) {
    restrictedUpdate.isFinalist = publicUpdate.isFinalist;
    delete publicUpdate.isFinalist;
  }
  if ('isWinner' in publicUpdate) {
    restrictedUpdate.isWinner = publicUpdate.isWinner;
    delete publicUpdate.isWinner;
  }
  return { publicUpdate, restrictedUpdate };
}

export function overlayRoomRestricted(
    room: TerpecaRoom, restricted?: RoomRestrictedFields | null): TerpecaRoom {
  if (!restricted) {
    return room;
  }
  return {
    ...room,
    nominations: restricted.nominations || [],
    resultsData: restricted.resultsData,
    isNominee: restricted.isNominee || [],
    isFinalist: restricted.isFinalist || [],
    isWinner: restricted.isWinner || []
  };
}

export function announcedNomineeYears(
    currentYear: number, votingOpen: boolean, winnersAnnounced: boolean): number[] {
  const years: number[] = [];
  for (let year = MIN_AWARD_YEAR; year < currentYear; ++year) {
    years.push(year);
  }
  if (votingOpen || winnersAnnounced) {
    years.push(currentYear);
  }
  return years;
}

export function announcedWinnerYears(currentYear: number, winnersAnnounced: boolean): number[] {
  const years: number[] = [];
  for (let year = MIN_AWARD_YEAR; year < currentYear; ++year) {
    years.push(year);
  }
  if (winnersAnnounced) {
    years.push(currentYear);
  }
  return years;
}

export function publicAwardYearsFromSettings(
    currentYear: number, settings: TerpecaSettings | undefined): {
  nomineeYears: number[];
  winnerYears: number[];
} {
  const votingOpen = Boolean(settings?.votingOpen);
  const winnersAnnounced = Boolean(settings?.sendWinnerEmails);
  return {
    nomineeYears: announcedNomineeYears(currentYear, votingOpen, winnersAnnounced),
    winnerYears: announcedWinnerYears(currentYear, winnersAnnounced)
  };
}

export function filterYears(values: number[] | undefined, allowed: number[]): number[] {
  const allowedSet = new Set(allowed);
  return (values || []).filter(year => allowedSet.has(year));
}

export function publicRoomAwardFields(
    restricted: RoomRestrictedFields | undefined, nomineeYears: number[], winnerYears: number[]): {
  isNominee: number[];
  isFinalist: number[];
  isWinner: number[];
} {
  return {
    isNominee: filterYears(restricted?.isNominee, nomineeYears),
    isFinalist: filterYears(restricted?.isFinalist, nomineeYears),
    isWinner: filterYears(restricted?.isWinner, winnerYears)
  };
}
